import React, { useEffect, useMemo, useRef, useState } from "react";
import { Edit2, FilePenLine, Truck } from "lucide-react";
import { formatDateInUS, getCurrentUser, getRemainingTrialDays } from "../utils/utils";
import { useLocation } from "react-router-dom";
import CustomerDocuments from "./UIElement/CustomerDocuments";
import Swal from "sweetalert2";
import { SlideTabsRef } from "./UIElement/SlideTabs";
import CustomerInfo from "./Customer/CustomerInfo";
import CustomerNotes from "./Customer/CustomerNotes";
import BusinessInfo from "./UIElement/BusinessInfo";
import { AdminUser } from "../models/AdminUser";
import CustomerActivity from "./Customer/CustomerActivity";
import PaymentSection from "./Customer/CustomerPayment";
import PermissionSelector from "./Customer/PermissionSelector";
import TrailUserNotes from "./Customer/TrailUserNotes";
import AccessTab from "./Customer/AccessTab";
import { getDefaultPermissions } from "../utils/lead-utils";
import StatusDropdown from "./UIElement/StatusDropdown";
import { ApiUrl } from "../config/apiConfig";
import { postData } from "../services/Axios/Axios";
import { sendAccountStatusChangeMail } from "../functions/accountFunctions";
import UnderlineSlidingTabs from "./UIElement/UnderlineSlidingTabs";
import messageService from "../services/message/messageService";
import { Student } from "../types/types";

type Customer = {
  user?: AdminUser | null;
  selectedCustomer: any;
  setSelectedCustomer: React.Dispatch<any>;
  onCustomerUpdateSuccess: () => Promise<void>;
  onClose?: () => void;
  leadId?: string;
  activeButton?: number;
  setPermissions?: (updated: string[]) => void;
  hideStatusBtn?: boolean;
};

export default function Customer({
  user,
  selectedCustomer,
  setSelectedCustomer,
  onCustomerUpdateSuccess,
  onClose,
  leadId,
  activeButton,
  setPermissions = () => { },
  hideStatusBtn = false
}: Customer) {
  const { pathname } = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [tab, setTab] = useState("information");
  const slideTabsRef = useRef<SlideTabsRef>(null);
  const [student, setStudent] = useState<Student>(
    selectedCustomer || ({} as Student)
  );
  const tabs = useMemo(() => {
    let baseTabs = [
      { label: "Information", value: "information" },
      { label: "Notes", value: "notes" },
    ];

    if (pathname.includes("/dot-compliance")) {
      baseTabs.push(
        { label: "Business Info", value: "businessInfo" },
        { label: "Payments", value: "payments" },

        { label: "Documents", value: "documents" },
        { label: "Activity", value: "activity" },
      );
    }
    if (pathname.includes("/trail-users")) {
      const currentUser = getCurrentUser()
      baseTabs = [
        { label: "Information", value: "information" },
        { label: "Notes", value: "notes" },
        { label: "Permissions", value: "permissions" },
        { label: "Access", value: "access" }
      ];

      

      if (selectedCustomer?.confirm_account === "PENDING") {
        baseTabs = [
          { label: "Information", value: "information" },
          { label: "Notes", value: "notes" },
        ];
      }
    }
    return baseTabs;
  }, [pathname, selectedCustomer?._id]);

  const scheduleMeeting = async () => {
    console.log("Scheduling meeting...");
  };
  const userTypes: Record<string, string> = {
    trailUser: "bg-gradient-to-r from-[#60a5fa] to-[#3b82f6] text-white",
    student: "bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] text-white",
    b2b: "bg-gradient-to-r from-[#34d399] to-[#059669] text-white",
    Rejected: "bg-gradient-to-r from-[#f87171] to-[#dc2626] text-white",
    "Select Type": "bg-gradient-to-r from-[#fdba74] to-[#fb923c] text-white",
    "Reset Trial": "bg-gradient-to-r from-[#818cf8] to-[#4338ca] text-white"
  };

  const DOT_STATUS_LIST = [
    "New Entry",
    "In-Progress",
    "Closed",
    "Business Entry"
  ];

  const DOT_STATUS_COLORS = {
    "New Entry": "bg-blue-100 text-blue-700",
    "In-Progress": "bg-yellow-100 text-yellow-700",
    "Closed": "bg-green-100 text-green-700",
    "Business Entry": "bg-purple-100 text-purple-700",
    default: "bg-gray-200 text-gray-700"
  };
  const cloneUser = async () => {
    try {
      await postData(ApiUrl.cloneCustomer + selectedCustomer._id, {});
    } catch (err) {
      console.error(err);
    }
  };

  const clearUserData = async () => {
    try {
      await postData(ApiUrl.deleteCustomerData + selectedCustomer._id, {});
    } catch (err) {
      console.error(err);
    }
  };



  const updateUserType = async (data: any, selectedValue: any) => {

    let obj: any = {
      _id: data._id,
      userType: selectedValue
    };

    // if fresh then auto approve after selection type
    if (selectedCustomer?.confirm_account === "PENDING" && selectedValue !== "Rejected") {
      obj["confirm_account"] = "CONFIRMED"
    }


    if (selectedValue === "Rejected") {
      obj["confirm_account"] = "DENIED"
    }

    try {

      await postData(ApiUrl.managedCompanies, obj);
      if (selectedCustomer?.confirm_account === "PENDING" && selectedValue !== "Rejected") {
        sendAccountStatusChangeMail(selectedCustomer.email, selectedCustomer.first_name, "CONFIRMED", selectedCustomer.password)
      }

      if (selectedValue === "student") {
        // clone user in case student
        cloneUser();
      }



      if (selectedValue === "b2b") {
        // clear user in case student
        clearUserData();
      }

      const messageServices = messageService();


      messageServices.toast("success", "User Type Updated Successfully!");
      onCustomerUpdateSuccess();
      if (onClose) {
        onClose()
      }
    } catch (error) {
      console.error("Error posting data:", error);
    }
  };

  const getUserTypeListByType = (type: string, isApprovalPending: boolean = false) => {
    if (isApprovalPending) return ["trailUser", "student", "b2b", "Rejected"]

    switch (type) {
      case "trailUser":

        return ["Reset Trial", "student", "b2b", "Rejected",]
      case "student":

        return ["b2b", "Rejected"]

      case "b2b":

        return ["Rejected"]

      default:
        return ["trailUser", "student", "Rejected"]
    }

  }


  const renewTrial = async () => {
    try {
      await postData(ApiUrl.renewTrial + selectedCustomer._id, {});

      const messageServices = messageService();

      messageServices.toast("success", "Trial Renewed !");
      onCustomerUpdateSuccess();

      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteUser = async () => {
    const obj = {
      _id: selectedCustomer._id,
      isDeleted: !selectedCustomer.isDeleted,
    };
    let response = postData(ApiUrl.deletecomapnyAndItsUsers, obj);
    if (await response) {
      onCustomerUpdateSuccess();

      if (onClose) {
        onClose();
      }
    }
  }

  useEffect(() => {
    if (slideTabsRef.current) {
      slideTabsRef.current.resetToFirstTab();
    }
  }, [selectedCustomer?._id]);

  return (
    <div className="max-w-[1400px] bg-[#FAFDFF] h-full mx-auto ">
      <div className="w-full  rounded-xl  ">
        {/* Header */}
        <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between pl-5 pr-2 pt-4 pb-3 border-b border-gray-100 bg-white">
          <div className="w-full flex items-start gap-3.5">
            <div className="flex items-start gap-3 flex-1 min-w-[250px]">
              <div className="w-[68px] h-[68px] shadow-circle hover:shadow-md ease-in-out overflow-hidden  rounded-full flex items-center justify-center flex-shrink-0">
                <img src="/assets/img/person-img.png" alt="maleIcon" className='w-full h-full p-4' />
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <h1 className="text-[20px] w-[22rem] whitespace-normal font-semibold text-gray-900 leading-tight mb-0.5">{`${selectedCustomer?.first_name ||
                  selectedCustomer?.firstName ||
                  selectedCustomer?.name ||
                  ""
                  } ${selectedCustomer?.last_name || selectedCustomer?.lastName || ""
                  }`}

                  {/* <span
                className={`
                inline-flex items-center justify-center
                px-2 py-[2px] text-xs !capitalize font-medium
                rounded-full border
                ${selectedCustomer.confirm_account === "CONFIRMED"
                    ? "border-green-300 bg-green-50 text-green-600"
                    : selectedCustomer.confirm_account === "DENIED"
                      ? "border-red-300 bg-red-50 text-red-600"
                      : selectedCustomer.confirm_account === "PENDING"
                        ? "border-yellow-300 bg-yellow-50 text-yellow-600"
                        : "border-gray-300 bg-gray-50 text-gray-600"
                  }
              `}
              >
                {capitalizeFirstLetter(selectedCustomer.confirm_account)}
              </span> */}
                </h1>
                <p className="text-[14px] text-gray-500 leading-[1.4]">
                  User ID: {selectedCustomer?._id}
                </p>
                <p className="text-[14px] text-gray-500 leading-[1.4]">
                  Registered: {formatDateInUS(selectedCustomer?.createdAt)}
                </p>
                {selectedCustomer?.lastLoginTime && (
                  <p className="text-[14px] text-gray-500 leading-[1.4]">
                    Last Activity:{" "}
                    {formatDateInUS(selectedCustomer?.lastLoginTime)}
                  </p>
                )}
                {selectedCustomer?.trialStartDate && (
                  <p className="text-[14px] text-gray-500 leading-[1.4]">
                    Expire in:{" "}
                    {getRemainingTrialDays(selectedCustomer?.trialStartDate) > 0
                      ? `${getRemainingTrialDays(
                        selectedCustomer?.trialStartDate
                      )} Days`
                      : `Expired`}
                  </p>
                )}
              </div>
            </div>

            {(pathname === "/trail-users" || pathname === "/dot-compliance") && (
              <div className="flex flex-col items-start">

                {/* Company Type */}
                <div className="flex items-center gap-2 text-[#ff914d] font-medium">
                  <div className="flex gap-3">
                    <Truck fill="#ff914d" stroke="#ff914d" />
                    <span className="text-sm capitalize">
                      {selectedCustomer?.company_type}
                    </span>
                  </div>
                </div>

                {/* Dropdown */}
                {!hideStatusBtn && (
                  <div className="mt-2">

                    {/* ================= TRAIL USERS ================= */}
                    {pathname === "/trail-users" && (
                      <StatusDropdown
                        selectedValue={
                          selectedCustomer?.confirm_account === "PENDING"
                            ? "Select Type"
                            : selectedCustomer?.userType || "trailUser"
                        }
                        statusList={getUserTypeListByType(
                          selectedCustomer?.userType,
                          selectedCustomer?.confirm_account === "PENDING"
                        )}
                        onChange={(value) => {
                          const newValue = value;
                          const oldValue = selectedCustomer?.userType;

                          setSelectedCustomer((prev: any) => ({
                            ...prev,
                            userType: newValue,
                          }));

                          Swal.fire({
                            title: "Are you sure?",
                            text: "Are You Sure You Want To Change Type",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonText: "Yes",
                            cancelButtonText: "Cancel",
                            allowOutsideClick: false,
                          }).then((result) => {
                            if (result.isConfirmed) {
                              if (newValue === "Reset Trial") {
                                renewTrial();
                              } else if (newValue === "Rejected") {
                                deleteUser();
                              } else {
                                updateUserType(selectedCustomer, newValue);
                              }
                            } else {
                              setSelectedCustomer((prev: any) => ({
                                ...prev,
                                userType: oldValue,
                              }));
                            }
                          });
                        }}
                        statusColors={userTypes}
                      />
                    )}

                    {/* ================= DOT COMPLIANCE ================= */}
                    {pathname === "/dot-compliance" && (
                      <StatusDropdown
                        selectedValue={selectedCustomer?.dotStatus || "New Entry"} // default visible
                        statusList={DOT_STATUS_LIST}
                        onChange={(value) => {
                          const newValue = value;
                          const oldValue = selectedCustomer?.dotStatus;

                          setSelectedCustomer((prev: any) => ({
                            ...prev,
                            dotStatus: newValue,
                          }));

                          Swal.fire({
                            title: "Are you sure?",
                            text: "Change DOT Status?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonText: "Yes",
                            cancelButtonText: "Cancel",
                          }).then((result) => {
                            if (result.isConfirmed) {
                              // 👉 call separate API if needed
                              updateUserType(selectedCustomer, newValue);
                            } else {
                              setSelectedCustomer((prev: any) => ({
                                ...prev,
                                dotStatus: oldValue,
                              }));
                            }
                          });
                        }}
                        statusColors={DOT_STATUS_COLORS}
                      />
                    )}

                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className='relative'>
          <UnderlineSlidingTabs ref={slideTabsRef} onChange={(value) => setTab(value)} tabs={tabs} />

          {tab === "information" && pathname === "/dot-compliance" &&
            <button onClick={() => {
              setIsEditMode(!isEditMode);


            }} className="flex  absolute top-[3px] w-[80px] right-[16px] items-center gap-1.5 px-3 py-1.5 text-[12.5px] text-greenBg bg-teal-50 hover:bg-teal-100 rounded-md border border-gray-300 hover:border-greenBg my-2">
              {isEditMode ? <> <FilePenLine size={16} /> View </> : <><Edit2 size={14} /> Edit </>}
            </button>
          }
        </div>
        {tab === "information" && pathname === "/trail-users" && (
          <CustomerInfo
            selectedCustomer={selectedCustomer}
          />
        )}

        {tab === "notes" &&
          pathname === "/trail-users" && (
            <TrailUserNotes
              onCustomerUpdateSuccess={onCustomerUpdateSuccess}
              setSelectedCustomer={setSelectedCustomer}
              selectedCustomer={selectedCustomer}
              customStyle="!mt-0"
            ></TrailUserNotes>
          )}

        {tab === "access" &&
          pathname === "/trail-users" && (
            <AccessTab
              selectedCustomer={selectedCustomer}
              setSelectedCustomer={setSelectedCustomer}
            ></AccessTab>
          )}

        {tab === "permissions" &&
          pathname === "/trail-users" && (
            <PermissionSelector
              permissions={selectedCustomer.permissions ?? ""}
              setPermissions={setPermissions}
              defaultPermissions={getDefaultPermissions(selectedCustomer.source).map((p) => p.label)}
              selectedCustomer={selectedCustomer}
            ></PermissionSelector>
          )}




     


        {tab === "notes" &&
          pathname === "/hr" &&
          user?.hrPermissions?.includes("Notes") && (
            <CustomerNotes
              onCustomerUpdateSuccess={onCustomerUpdateSuccess}
              setSelectedCustomer={setSelectedCustomer}
              selectedCustomer={selectedCustomer}
            ></CustomerNotes>
          )}

        {tab === "notes" &&
          pathname === "/dot-compliance" &&
          user?.dotPermissions?.includes("Notes") && (
            <CustomerNotes
              onCustomerUpdateSuccess={onCustomerUpdateSuccess}
              setSelectedCustomer={setSelectedCustomer}
              selectedCustomer={selectedCustomer}
            ></CustomerNotes>
          )}

        {tab === "activity" &&
          pathname === "/dot-compliance" && (
            <CustomerActivity
              student={student}
              setStudent={setStudent}
              scheduleMeeting={scheduleMeeting}
              customerId={selectedCustomer?._id}
              selectedCustomer={selectedCustomer}
              onCustomerUpdateSuccess={onCustomerUpdateSuccess}
            />
          )
          // )
        }

        {tab === "activity" &&
          pathname === "/hr" &&
          user?.hrPermissions?.includes("Activity") && (
            <CustomerActivity
              customerId={selectedCustomer?._id}
              selectedCustomer={selectedCustomer}
            ></CustomerActivity>
          )}
        {tab === "businessInfo" &&
          // user?.dotPermissions?.includes("Business Info") && (
          <BusinessInfo data={selectedCustomer?.DotId} />
          // )
        }


        {tab === "payments" && (
          <PaymentSection leadId={selectedCustomer?._id} />
        )}

        {tab === "documents" && (
          <CustomerDocuments />
        )}


        {tab === "notes" && pathname === "/candidate/newcandidate" && (
          <CustomerNotes
            onCustomerUpdateSuccess={onCustomerUpdateSuccess}
            setSelectedCustomer={setSelectedCustomer}
            selectedCustomer={selectedCustomer}
            activeButton={activeButton}
          ></CustomerNotes>
        )}

        {tab === "activity" && pathname === "/candidate/newcandidate" && (
          <CustomerActivity
            customerId={selectedCustomer?._id}
            selectedCustomer={selectedCustomer}
          ></CustomerActivity>
        )}
      </div>
    </div>
  );
}
